// src/render/animate.ts
// requestAnimationFrame tween driver (spec §5.4 "juice"). A slide eases out with a tiny
// settle/overshoot ("clack"); an exit slides to the gate then fades + scales through it. Honors
// prefers-reduced-motion: tweens collapse to near-instant fades with no overshoot. Every animation
// resolves a Promise so `main` can gate input while one is in flight.

import type { BlockAnim } from './canvas';
import type { BlockId, Delta } from '../game/types';

/** Called every frame with the current per-block animation (or null to clear). */
export type DrawFn = (anim: BlockAnim | null) => void;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function easeOutCubic(t: number): number {
  const x = 1 - t;
  return 1 - x * x * x;
}

/** Ease-out with a small overshoot then settle. Returns exactly 1 at t === 1 (no residual drift). */
function easeOutBack(t: number, overshoot: number): number {
  const c1 = overshoot;
  const c3 = c1 + 1;
  const x = t - 1;
  return 1 + c3 * x * x * x + c1 * x * x;
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export class Animator {
  private readonly draw: DrawFn;
  private rafId: number | null = null;
  private cancelCurrent: (() => void) | null = null;

  constructor(draw: DrawFn) {
    this.draw = draw;
  }

  get running(): boolean {
    return this.cancelCurrent !== null;
  }

  /** Stop any in-flight animation immediately (resolves its promise). */
  cancel(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const c = this.cancelCurrent;
    this.cancelCurrent = null;
    if (c !== null) {
      c();
    }
  }

  /**
   * Animate a 'slid' result: the block travels `distance` cells along `delta`. Ease-out with a tiny
   * clack; reduced motion resolves near-instantly with no overshoot.
   */
  slide(blockId: BlockId, delta: Delta, distance: number, reduced: boolean): Promise<void> {
    if (reduced || distance <= 0) {
      // Near-instant: show the final position for a frame, then resolve.
      return this.runTween(1, () => ({
        blockId,
        offsetRow: delta.drow * distance,
        offsetCol: delta.dcol * distance,
        alpha: 1,
        scale: 1,
      }));
    }
    const duration = Math.min(200, 120 + Math.min(distance, 4) * 14);
    return this.runTween(duration, (t) => {
      const e = easeOutBack(t, 1.1);
      return {
        blockId,
        offsetRow: delta.drow * distance * e,
        offsetCol: delta.dcol * distance * e,
        alpha: 1,
        scale: 1,
      };
    });
  }

  /**
   * Animate an 'exited' result: slide `distance` cells to the gate, then continue outward while
   * fading + shrinking through it. Reduced motion: a quick fade in place, no travel.
   */
  async exit(blockId: BlockId, delta: Delta, distance: number, reduced: boolean): Promise<void> {
    if (reduced) {
      await this.runTween(120, (t) => ({
        blockId,
        offsetRow: delta.drow * distance,
        offsetCol: delta.dcol * distance,
        alpha: 1 - clamp01(t),
        scale: 1,
      }));
      return;
    }
    // Phase A: slide up to the gate.
    if (distance > 0) {
      const durA = Math.min(180, 110 + Math.min(distance, 4) * 14);
      await this.runTween(durA, (t) => {
        const e = easeOutCubic(t);
        return {
          blockId,
          offsetRow: delta.drow * distance * e,
          offsetCol: delta.dcol * distance * e,
          alpha: 1,
          scale: 1,
        };
      });
    }
    // Phase B: pop through the gate (fade + scale + a touch more travel).
    const extra = 0.85;
    await this.runTween(170, (t) => {
      const e = easeOutCubic(t);
      return {
        blockId,
        offsetRow: delta.drow * (distance + extra * e),
        offsetCol: delta.dcol * (distance + extra * e),
        alpha: 1 - clamp01(t),
        scale: 1 - 0.45 * e,
      };
    });
  }

  /** Drives one tween over `durationMs`, invoking draw(frame(p)) each rAF; resolves at p === 1. */
  private runTween(
    durationMs: number,
    frame: (t: number) => BlockAnim,
  ): Promise<void> {
    // Ensure no overlap: cancel any prior tween first.
    this.cancel();
    return new Promise<void>((resolve) => {
      const start = now();
      let settled = false;
      const finish = (): void => {
        if (settled) {
          return;
        }
        settled = true;
        this.rafId = null;
        this.cancelCurrent = null;
        resolve();
      };
      this.cancelCurrent = finish;
      const tick = (): void => {
        if (settled) {
          return;
        }
        const elapsed = now() - start;
        const t = durationMs <= 0 ? 1 : clamp01(elapsed / durationMs);
        this.draw(frame(t));
        if (t >= 1) {
          finish();
          return;
        }
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    });
  }
}
